import React from 'react';
import { TrendingUp, Scale, CheckCircle, Lock, AlertCircle } from 'lucide-react';

/**
 * Affichage HONNÊTE du score : le score brut IA (0-9.5) est converti en /100
 * sans aucun "mapping psychologique". Le chiffre affiché EST le résultat de l'analyse.
 *
 * Copywriting : audience en situation de stress, lecture mobile, pas de vocabulaire
 * juridique. Phrases courtes, mots simples (niveau CM2), zéro jargon.
 * La persuasion repose sur des leviers 100% factuels :
 * - Curiosity gap honnête : le score dit COMBIEN, pas POURQUOI ni QUOI FAIRE (→ LV3)
 * - Urgence réelle : passé certains délais, il est trop tard pour agir
 * - Réconfort véridique : même un dossier faible a des solutions (négociation, preuves, frais)
 */
export const transformScore = (rawScore) => {
  // Conversion simple 0-9.5 → 0-100. Aucune inflation.
  return Math.round(Math.max(0, Math.min(100, (rawScore || 0) * 10)));
};

/**
 * Un score absent n'est PAS un score de zéro.
 *
 * `transformScore` fait `rawScore || 0` : un dossier dont l'analyse a échoué
 * afficherait 0 %, c'est-à-dire le pire résultat possible, avec le message
 * « Peu de failles trouvées ». On distingue donc explicitement les deux états —
 * le backend renvoie désormais score=null et score_status='failed' plutôt que
 * d'inventer un 5.0 (cf. pipeline/sanitizer.py).
 */
export const isScoreExploitable = (rawScore) =>
  typeof rawScore === 'number' && Number.isFinite(rawScore);

const ScoreIndisponible = () => (
  <div className="text-center py-6">
    <div
      className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
      style={{ backgroundColor: '#fef3c7' }}
    >
      <AlertCircle size={30} style={{ color: '#b45309' }} />
    </div>
    <h3 className="text-xl font-bold mb-2" style={{ color: '#92400e' }}>
      Votre analyse n'a pas pu aboutir
    </h3>
    <p className="mx-auto max-w-md text-gray-600 leading-relaxed">
      Nous n'avons pas réussi à exploiter les documents transmis. Cela arrive quand un
      scan est trop peu lisible ou qu'une pièce est incomplète. Vous pouvez ajouter une
      photo plus nette — nous relançons l'analyse sans frais.
    </p>
  </div>
);

const ScoreDisplay = ({ score }) => {
  if (!isScoreExploitable(score)) return <ScoreIndisponible />;

  const displayScore = transformScore(score);

  const getScoreColor = () => {
    if (displayScore < 40) return 'score-moderate';
    if (displayScore < 70) return 'score-strong';
    return 'score-excellent';
  };

  const getScoreInfo = () => {
    if (displayScore < 40) {
      return {
        title: 'Peu de failles trouvées — mais des solutions existent',
        description:
          "Notre analyse n'a pas trouvé de faille importante dans votre dossier. " +
          "C'est fréquent, et ça ne veut pas dire qu'il n'y a rien à faire. " +
          "Vous pouvez souvent négocier le montant, exiger les preuves de la dette, " +
          "et refuser les frais ajoutés en plus.",
        color: '#b45309',
        icon: <TrendingUp size={32} />,
      };
    }
    if (displayScore < 70) {
      return {
        title: 'Votre dossier peut se défendre',
        description:
          "Notre analyse a trouvé des points à vérifier avant de payer quoi que ce soit : " +
          "les dates, la procédure, les montants réclamés. " +
          "Parfois, tout se joue à quelques semaines près sur une seule date. " +
          "C'est exactement ce que l'analyse complète vérifie pour vous.",
        color: '#1d4ed8',
        icon: <Scale size={32} />,
      };
    }
    return {
      title: 'De bons signaux pour vous',
      description:
        "Notre analyse a trouvé plusieurs éléments qui peuvent jouer en votre faveur. " +
        "Attention : passé certains délais, il est souvent trop tard pour agir. " +
        "Plus vous vous en occupez tôt, plus vous gardez la main.",
      color: '#065f46',
      icon: <CheckCircle size={32} />,
    };
  };

  const scoreInfo = getScoreInfo();

  return (
    <div data-testid="score-display" className="text-center">
      <div className={`score-circle ${getScoreColor()} mx-auto mb-2`} data-testid="score-circle">
        {displayScore}<span style={{ fontSize: '0.5em' }}>/100</span>
      </div>
      <p className="text-sm text-gray-500 mb-8">Le score de votre dossier face au recouvrement</p>

      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center mb-4" style={{ color: scoreInfo.color }}>
          {scoreInfo.icon}
          <h2 className="text-3xl font-bold ml-3" style={{ color: scoreInfo.color }}>
            {scoreInfo.title}
          </h2>
        </div>

        <p className="text-lg text-gray-700 mb-6">
          {scoreInfo.description}
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left">
          <h3 className="font-semibold text-blue-900 mb-3">Comment lire ce score</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2" style={{ color: '#b45309' }}>&#9679;</span>
              <span><strong>0-39 :</strong> pas de faille importante trouvée — mais négocier, demander des preuves et refuser les frais reste possible</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2" style={{ color: '#1d4ed8' }}>&#9679;</span>
              <span><strong>40-69 :</strong> des points sérieux à vérifier avant de payer quoi que ce soit</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2" style={{ color: '#065f46' }}>&#9679;</span>
              <span><strong>70-100 :</strong> de bons signaux pour vous — à confirmer vite</span>
            </li>
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-left mt-4">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
            <Lock size={16} className="mr-2" />
            Ce que ce score ne vous dit pas encore
          </h3>
          <p className="text-gray-700">
            Le score vous dit si votre dossier peut se défendre. Il ne vous dit pas encore
            <strong> pourquoi</strong>, <strong>quoi faire</strong>, ni <strong>avant quelle date</strong>.
            C'est ce que l'analyse complète vous donne.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScoreDisplay;
